import type { ContractDetail, HistoryEntry, Lookups, Me } from '@ft/shared';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ContractForm } from '../components/ContractForm';
import { DeleteButton } from '../components/DeleteButton';
import { Loading, useLoad, useToast } from '../components/ui';
import { Chip, Panel } from '../lib/collapse';
import { useI18n, type T } from '../i18n';
import { getAreaId, switchArea } from '../lib/area';
import { openFile, upload } from '../lib/download';
import { dfmt, dtfmt, eur2, pct } from '../lib/format';
import { api } from '../lib/http';
import { rememberedQuery } from '../lib/remember';
import { BudgetBar, ContractStatePill, termPos, TypePill, UseBar } from './ContractsPage';

const FIELD: Record<string, string> = {
  name: 'cf.name', contract_number: 'cf.number', customer_id: 'common.customer', valid_from: 'cf.from', valid_to: 'cf.to',
  budget_cents: 'cf.budget', sharepoint_url: 'cf.link', note: 'of.note', pdf_name: 'ct.pdf', projects: 'cf.projects', years: 'cf.years', external: 'cf.ext',
};

function describe(h: HistoryEntry, t: T): string {
  if (h.action !== 'update') return '';
  return Object.keys(h.changes).map((k) => (FIELD[k] ? t(FIELD[k] as 'cf.name') : k)).join(', ');
}

/** Detail eines Hauptvertrags: Budget und Verbrauch je Jahr und Projekt, zählende Bestellungen, Ablage, Verlauf. */
export function ContractPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const { id } = useParams();
  const nav = useNavigate();
  const { data: c, error, reload } = useLoad<ContractDetail>(`/api/contracts/${id}`);
  const { data: lookups } = useLoad<Lookups>('/api/lookups');
  const [edit, setEdit] = useState(false);
  const [delErr, setDelErr] = useState<string | null>(null);
  const [toast, say] = useToast();

  if (!c) return <section className="page"><Loading error={error} /></section>;
  // Kopf, Ablage, Löschen: angelegt von, Admins, Delivery Heads; Projekte zuordnen: PM/Admin des gewählten Bereichs
  const canEdit = c.mayEdit;
  const canDel = c.mayEdit;
  const visible = new Set(me.areas.map((a) => a.id));
  const openOrder = (o: { id: string; area: { id: string } }) => {
    if (o.area.id === getAreaId()) nav(`/bestellungen/${o.id}`);
    else if (visible.has(o.area.id)) switchArea(o.area.id, `/bestellungen/${o.id}`);
  };
  const hasYearBudgets = c.years.some((y) => y.budgetCents !== null);
  const conflicts = c.years.filter((y) => y.manualOrderedCents !== null && y.toolOrderedCents > 0);
  const hasShares = c.projects.some((p) => p.budgetCents !== null);

  async function archive() {
    setDelErr(null);
    try { await api('DELETE', `/api/contracts/${c!.id}?version=${c!.version}`); nav(`/vertraege${rememberedQuery('contracts')}`); } catch (e) { setDelErr((e as Error).message); }
  }

  return (
    <section className="page">
      <div className="crumbs"><Link to={`/vertraege${rememberedQuery('contracts')}`}>{t('nav.contracts')}</Link><span>›</span><span>{c.name}</span></div>

      <div className="cthead">
        <div>
          <div className="lbl" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>{t('ct.kind')} <TypePill c={c} t={t} /> <ContractStatePill c={c} t={t} /></div>
          <h1>{c.name}</h1>
          <div className="sub">{c.customer.name}{c.contractNumber ? <> · <span className="mono">{c.contractNumber}</span></> : null} · {t('ct.homeArea', { area: c.homeArea.name })}{c.contractType && c.billingCycle ? ` · ${t('cf.cycle')}: ${t(`cf.cycle.${c.billingCycle}`)}` : ''}</div>
          <div className="kv">
            <div><small>{t('ct.col.budget')}</small><b className="num">{c.budgetCents !== null ? eur2(c.budgetCents) : '–'}</b>{c.budgetCents !== null && !c.budgetSet && <div className="sub small">{t('ct.budgetDerived')}</div>}</div>
            <div><small>{t('ct.col.ordered')}</small><b className="num">{eur2(c.orderedCents)}</b>{c.manualOrderedCents > 0 && <div className="sub small">{t('ct.manualIncl', { sum: eur2(c.manualOrderedCents) })}</div>}</div>
            <div><small>{t('ct.col.billed')}</small><b className="num">{eur2(c.billedCents)}</b></div>
            <div><small>{t('ct.col.rest')}</small><b className="num" style={{ color: c.restCents !== null && c.restCents < 0 ? '#ff9b9b' : '#7fe0b0' }}>{c.restCents !== null ? eur2(c.restCents) : '–'}</b></div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 14 }}>
          <div>
            <div className="lbl">{t('ct.col.usage')}</div>
            <UseBar c={c} />
            <div className="ctlbl">{c.budgetCents ? t('ct.card.use', { o: pct(c.orderedCents / c.budgetCents), b: pct(c.billedCents / c.budgetCents) }) : t('ct.card.noBudget')}</div>
          </div>
          <div>
            <div className="lbl">{t('ct.col.term')}</div>
            {termPos(c) !== null ? <div className="cttime" style={{ marginTop: 6 }}><i style={{ width: `${termPos(c)! * 100}%` }} /><span style={{ left: `${termPos(c)! * 100}%` }} /></div> : null}
            <div className="ctlbl three"><span>{dfmt(c.validFrom)}</span>{termPos(c) !== null && <span>{t('ct.card.term', { p: pct(termPos(c)!) })}</span>}<span>{dfmt(c.validTo)}</span></div>
          </div>
        </div>
      </div>

      <div className={`notice ${c.filed ? 'ok' : 'warn'}`} role="status">
        {c.filed ? t('ct.filedOk') : t('ct.filedMissing', { what: [!c.sharepointUrl && t('ct.missingLink'), !c.hasPdf && t('ct.missingPdf')].filter(Boolean).join(t('ct.and')) })}
      </div>

      <div className="grid2">
        <div className="col">
          <Panel id="ct.areas" title={t('ct.areasTitle')} hint={t('ct.areasHint')} summary={<Chip>{t('ct.col.ordered')} <b>{eur2(c.orderedCents)}</b> · {t('ct.col.billed')} <b>{eur2(c.billedCents)}</b></Chip>}>
            <div className="tablewrap">
              {!c.areas.length && !c.external.length && !c.manualOrderedCents ? <div className="empty">{t('ct.noProjects')}</div> : (
                <table>
                  <thead><tr><th>{t('ct.col.area')}</th><th className="r">{t('ct.col.projects')}</th><th className="r">{t('ct.col.ordered')}</th><th className="r">{t('ct.col.billed')}</th></tr></thead>
                  <tbody>
                    {c.areas.map((a) => (
                      <tr key={a.area.id}><td>{a.area.name}</td><td className="r num">{a.projectCount}</td><td className="r num">{eur2(a.orderedCents)}</td><td className="r num">{eur2(a.billedCents)}</td></tr>
                    ))}
                    {c.external.length > 0 && <tr><td>{t('ct.extRow')}</td><td className="r num">–</td><td className="r num">{eur2(c.externalCents)}</td><td className="r num muted">–</td></tr>}
                    {c.manualOrderedCents > 0 && <tr><td>{t('ct.manualHint')}</td><td className="r num">–</td><td className="r num">{eur2(c.manualOrderedCents)}</td><td className="r num muted">–</td></tr>}
                  </tbody>
                  <tfoot><tr><td colSpan={2}>{t('common.sum')}</td><td className="r num">{eur2(c.orderedCents)}</td><td className="r num">{eur2(c.billedCents)}</td></tr></tfoot>
                </table>
              )}
            </div>
          </Panel>
          <Panel id="ct.projects" title={t('cf.projects')} hint={t('ct.projectsHint')} summary={<Chip>{t('order.projectsN', { n: c.projects.length })}</Chip>}>
            <div className="tablewrap">
              {!c.projects.length ? <div className="empty">{t('ct.noProjects')}</div> : (
                <table>
                  <thead><tr><th>{t('common.project')}</th><th>{t('ct.col.area')}</th><th>{t('common.pm')}</th>{hasShares && <th className="r">{t('ct.col.share')}</th>}<th className="r">{t('ct.col.ordered')}</th>{hasShares && <th className="r">{t('ct.col.rest')}</th>}<th className="r">{t('ct.col.billed')}</th></tr></thead>
                  <tbody>
                    {c.projects.map((p) => {
                      const rest = p.budgetCents !== null ? p.budgetCents - p.orderedCents : null;
                      return (
                        <tr key={p.project.id} className={p.area.id === getAreaId() ? 'click' : undefined} tabIndex={p.area.id === getAreaId() ? 0 : undefined}
                          onClick={() => { if (p.area.id === getAreaId()) nav(`/bestellungen?projekt=${encodeURIComponent(p.project.name)}`); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' && p.area.id === getAreaId()) nav(`/bestellungen?projekt=${encodeURIComponent(p.project.name)}`); }}>
                          <td>{p.project.name}</td>
                          <td className="small">{p.area.name}</td>
                          <td>{p.pm?.name ?? <span className="muted">–</span>}</td>
                          {hasShares && <td className="r num">{p.budgetCents !== null ? eur2(p.budgetCents) : '–'}</td>}
                          <td className="r num">{eur2(p.orderedCents)}</td>
                          {hasShares && <td className="r num" style={rest !== null && rest < 0 ? { color: 'var(--neg)' } : undefined}>{rest !== null ? eur2(rest) : '–'}</td>}
                          <td className="r num">{eur2(p.billedCents)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </Panel>
          <Panel id="ct.orders" title={t('ct.ordersTitle', { n: c.orders.length + c.hiddenOrders.reduce((x, h) => x + h.count, 0) })} hint={t('ct.ordersHint')} summary={<Chip><b>{eur2(c.orders.reduce((x, o) => x + o.cents, 0))}</b></Chip>}>
            <div className="tablewrap">
              {!c.orders.length ? <div className="empty">{t('ct.noOrders')}</div> : (
                <table>
                  <thead><tr><th>{t('orders.col.number')}</th><th>{t('orders.col.orderedOn')}</th><th>{t('ct.col.area')}</th><th>{t('common.project')}</th><th className="r">{t('ct.col.counts')}</th></tr></thead>
                  <tbody>
                    {c.orders.map((o) => {
                      const can = visible.has(o.area.id);
                      return (
                        <tr key={o.id} className={can ? 'click' : undefined} tabIndex={can ? 0 : undefined} onClick={() => openOrder(o)} onKeyDown={(e) => { if (e.key === 'Enter') openOrder(o); }}>
                          <td><span className={can ? 'idlink' : undefined}>{o.orderNumber ?? t('common.noNumber')}</span></td><td className="num">{dfmt(o.orderDate)}</td><td className="small">{o.area.name}</td><td className="small">{o.projects.join(', ')}</td><td className="r num">{eur2(o.cents)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot><tr><td colSpan={4}>{t('common.sum')}</td><td className="r num">{eur2(c.orders.reduce((x, o) => x + o.cents, 0))}</td></tr></tfoot>
                </table>
              )}
              {c.hiddenOrders.map((h) => <p key={h.area.id} className="muted small" style={{ padding: '8px 16px', margin: 0 }}>{t('ct.hiddenOrders', { n: h.count, area: h.area.name, sum: eur2(h.cents) })}</p>)}
            </div>
          </Panel>
        </div>

        <div className="col">
          <Panel id="ct.years" title={t('ct.yearsTitle')} hint={!hasYearBudgets ? t('ct.yearsHint') : undefined} summary={<>{c.years.slice(-2).map((y) => <Chip key={y.year}>{y.year} <b>{eur2(y.orderedCents)}</b></Chip>)}</>}>
            {conflicts.map((y) => <p key={y.year} className="notice warn" style={{ margin: '8px 16px' }}>{t('ct.manualConflict', { year: y.year, sum: eur2(y.toolOrderedCents) })}</p>)}
            <div className="tablewrap">
              {!c.years.length ? <div className="empty">{t('ct.noOrders')}</div> : (
                <table>
                  <thead><tr><th>{t('ct.col.year')}</th>{hasYearBudgets && <th className="r">{t('ct.col.yearBudget')}</th>}<th className="r">{t('ct.col.ordered')}</th>{hasYearBudgets && <th className="r">{t('ct.col.rest')}</th>}<th className="r">{t('ct.col.billed')}</th>{hasYearBudgets && <th style={{ minWidth: 110 }} />}</tr></thead>
                  <tbody>
                    {c.years.map((y) => {
                      const rest = y.budgetCents !== null ? y.budgetCents - y.orderedCents : null;
                      return (
                        <tr key={y.year}>
                          <td className="num">{y.year}</td>
                          {hasYearBudgets && <td className="r num">{y.budgetCents !== null ? eur2(y.budgetCents) : '–'}</td>}
                          <td className="r num">{eur2(y.orderedCents)}{y.manualOrderedCents !== null && <span className="pill neu" title={t('ct.manualHint')} style={{ marginLeft: 6 }}>{t('ct.manual')}</span>}</td>
                          {hasYearBudgets && <td className="r num" style={rest !== null && rest < 0 ? { color: 'var(--neg)' } : undefined}>{rest !== null ? eur2(rest) : '–'}</td>}
                          <td className="r num">{eur2(y.billedCents)}{y.manualBilledCents !== null && <span className="pill neu" title={t('ct.manualHint')} style={{ marginLeft: 6 }}>{t('ct.manual')}</span>}</td>
                          {hasYearBudgets && <td><BudgetBar ordered={y.orderedCents} budget={y.budgetCents} /></td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </Panel>

          {c.external.length > 0 && (
            <Panel id="ct.ext" title={t('ct.extTitle')} hint={t('ct.extHint')} summary={<Chip>{t('order.historyN', { n: c.external.length })} · <b>{eur2(c.externalCents)}</b></Chip>}>
              <div className="tablewrap">
                <table>
                  <thead><tr><th>{t('ct.col.area')}</th><th>{t('orders.col.orderedOn')}</th><th>{t('order.note')}</th><th className="r">{t('ct.col.ordered')}</th></tr></thead>
                  <tbody>{c.external.map((e, i) => <tr key={i}><td>{e.areaName}</td><td className="num">{e.date ? dfmt(e.date) : '–'}</td><td className="small">{e.note ?? ''}</td><td className="r num">{eur2(e.amountCents)}</td></tr>)}</tbody>
                  <tfoot><tr><td colSpan={3}>{t('common.sum')}</td><td className="r num">{eur2(c.externalCents)}</td></tr></tfoot>
                </table>
              </div>
            </Panel>
          )}

          <Panel id="ct.docs" title={t('ct.docs')} summary={<Chip>{c.pdfName ?? t('ct.missingPdf')}{c.sharepointUrl ? ' · SharePoint' : ''}</Chip>} actions={<>
              {c.pdfName && <button className="btn" onClick={() => openFile(`/api/contracts/${c.id}/pdf`).catch((e: Error) => say(e.message))}>{t('pdf.open')}</button>}
              {canEdit && (
                <label className="btn">
                  {c.pdfName ? t('pdf.replace') : t('pdf.upload')}
                  <input type="file" accept="application/pdf,.pdf" hidden onChange={async (e) => {
                    const f = e.target.files?.[0]; e.target.value = '';
                    if (!f) return;
                    try { await upload('PUT', `/api/contracts/${c.id}/pdf`, f); reload(); say(t('pdf.saved')); } catch (err) { say((err as Error).message); }
                  }} />
                </label>
              )}
            </>}>
            <dl className="kv">
              <dt>{t('cf.link')}</dt>
              <dd>{c.sharepointUrl ? <a href={c.sharepointUrl} target="_blank" rel="noreferrer">{t('ct.openSharepoint')}</a> : <span className="muted">{t('ct.missingLink')}</span>}</dd>
              <dt>{t('ct.pdf')}</dt>
              <dd>{c.pdfName ?? <span className="muted">{t('ct.missingPdf')}</span>}</dd>
            </dl>
          </Panel>

          {c.note && <div className="box"><div className="panel-h"><h2>{t('order.note')}</h2></div><p className="notebox">{c.note}</p></div>}

          <Panel id="ct.hist" title={t('order.history')} summary={c.history[0] ? <Chip>{t('order.historyN', { n: c.history.length })} · {dfmt(c.history[0].at.slice(0, 10))}</Chip> : undefined}>
            <ul className="log">
              {c.history.map((h, i) => (
                <li key={i}>
                  <span className="ld num">{dtfmt(h.at)}</span>
                  <i className="lm" />
                  <div>
                    <div>{t(`hist.${h.action}`, { what: t('ct.histWhat') })}</div>
                    <div className="muted small">{[describe(h, t), h.user].filter(Boolean).join(' · ')}</div>
                  </div>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      </div>

      {(canEdit || canDel || c.mayAssign) && (
        <div className="footbar">
          {delErr && <p className="err" style={{ marginRight: 'auto' }}>{delErr}</p>}
          {canDel && <DeleteButton what={c.name} onDelete={archive} />}
          {(canEdit || c.mayAssign) && <button className="btn primary" onClick={() => setEdit(true)} disabled={!lookups}>{canEdit ? t('common.edit') : t('ct.assign')}</button>}
        </div>
      )}

      {edit && lookups && <ContractForm contract={c} lookups={lookups} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); reload(); say(t('ct.saved')); }} />}
      {toast}
    </section>
  );
}
